const allowedMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']

/**
 * Fetches data from the API
 * @param {string} param0.url - The URL to fetch
 * @param {string} param0.method - The HTTP method to use. Defaults to GET.
 * @param {object} param0.data - The data to send in the request body
 * @param {object} param0.searchParams - The search params to add to the URL
 * @param {object} param0.config - The config to pass to fetch
 * @param {boolean} param0.shouldError - Whether or not to throw an error if the request fails. Defaults to true.
 * @returns The response from the API
 */
export default async function apiFetch({
  url,
  method = 'GET',
  data = null,
  searchParams = null,
  config = {},
  shouldError = true,
}) {
  if (!url) return

  method = method.toUpperCase()

  if (!allowedMethods.includes(method)) {
    throw new Error(`Invalid method: ${method}`)
  }

  const baseUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/api/v3` // TODO: Replace with NEXT_PUBLIC_API_URL before deployment
  let fullUrl = `${baseUrl}/${url.replace(/^\//, '')}`

  if (searchParams) {
    const params = new URLSearchParams()

    for (const [key, value] of Object.entries(searchParams)) {
      if (typeof value === 'object') {
        // If the value is an object or array, stringify it
        params.append(key, JSON.stringify(value))
      } else {
        // Otherwise, append the key-value pair directly
        params.append(key, value)
      }
    }

    fullUrl += `?${params.toString()}`
  }

  const result = await fetch(fullUrl, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...config.headers,
    },
    body: data
      ? data instanceof FormData
        ? data
        : JSON.stringify(data)
      : undefined,
    ...config,
  })

  const response = await getResponse(result)

  if (!['200', '204'].includes(String(result.status))) {
    if (!shouldError) return null

    throw new Error(response?.message || result.statusText, {
      cause: {
        code: result.status,
        message: response?.message || result.statusText,
        ...(response?.discordInvite
          ? { discordInvite: response.discordInvite }
          : {}),
      },
    })
  }

  return response
}

async function getResponse(result) {
  try {
    const text = await result.text()
    return text ? JSON.parse(text) : result
  } catch (error) {
    return result
  }
}
